import { Badge, Button, Input, Select, Skeleton, Table, Text } from '@pcrm/fe/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../../components/section';
import {
  ACTIVITY_PAGE_SIZE,
  type ActivityFilters,
  type ActivityItem,
  activityActorsQueryOptions,
  activityQueryOptions,
} from '../../../features/activity/activity.api';
import {
  actionLabel,
  actorLabel,
  changeValueLabel,
  entityLabel,
  fieldLabel,
  knownActions,
} from '../../../features/activity/activity-labels';
import { formatDateTime } from '../../../lib/format';

export const Route = createFileRoute('/_authenticated/setari/jurnal')({
  component: JurnalPagina,
});

/** Toate filtrele au opțiunea „fără filtru"; `Select` nu acceptă valoare goală. */
const ANY = '_toate';

/**
 * Jurnalul de activitate: cine ce a modificat, când și de pe ce IP.
 *
 * Filtrele stau în starea componentei, nu în URL. Alegere conștientă și limitată: un
 * jurnal filtrat nu e ceva ce se trimite prin link cuiva în Faza 1. Când va fi, filtrele
 * trec în `validateSearch` — mecanismul e deja folosit la `/login`.
 */
function JurnalPagina() {
  const [filters, setFilters] = useState<ActivityFilters>({ page: 1 });
  const { data, isPending, error } = useQuery(activityQueryOptions(filters));
  const actors = useQuery(activityActorsQueryOptions());

  /** Orice schimbare de filtru readuce la prima pagină: altfel ai vedea „pagina 4" goală. */
  const changeFilter = (patch: Partial<ActivityFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }));

  const lastPage = data ? Math.max(1, Math.ceil(data.total / ACTIVITY_PAGE_SIZE)) : 1;
  const isFiltered = Boolean(filters.userId || filters.action || filters.from || filters.to);

  return (
    <SectionContainer>
      <SectionHeader
        title="Jurnal de activitate"
        subtitle="Ore în fusul României."
        actions={
          isFiltered ? (
            <Button size="small" variant="transparent" onClick={() => setFilters({ page: 1 })}>
              Golește filtrele
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-end gap-3 px-6 py-4">
        <FilterField label="Operator" htmlFor="filtru-operator">
          <Select
            value={filters.userId ?? ANY}
            onValueChange={(value) => changeFilter({ userId: value === ANY ? undefined : value })}
          >
            <Select.Trigger id="filtru-operator" className="w-52">
              <Select.Value placeholder="Toți" />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value={ANY}>Toți</Select.Item>
              {(actors.data ?? []).map((actor) => (
                <Select.Item key={actor.id} value={actor.id}>
                  {actor.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </FilterField>

        <FilterField label="Acțiune" htmlFor="filtru-actiune">
          <Select
            value={filters.action ?? ANY}
            onValueChange={(value) => changeFilter({ action: value === ANY ? undefined : value })}
          >
            <Select.Trigger id="filtru-actiune" className="w-60">
              <Select.Value placeholder="Toate" />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value={ANY}>Toate</Select.Item>
              {knownActions.map((action) => (
                <Select.Item key={action.value} value={action.value}>
                  {action.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </FilterField>

        <FilterField label="De la" htmlFor="filtru-de-la">
          <Input
            id="filtru-de-la"
            type="date"
            className="w-40"
            value={filters.from ?? ''}
            onChange={(event) => changeFilter({ from: event.target.value || undefined })}
          />
        </FilterField>

        <FilterField label="Până la" htmlFor="filtru-pana-la">
          <Input
            id="filtru-pana-la"
            type="date"
            className="w-40"
            value={filters.to ?? ''}
            onChange={(event) => changeFilter({ to: event.target.value || undefined })}
          />
        </FilterField>
      </div>

      {isPending ? (
        <div className="flex flex-col gap-3 px-6 py-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : error ? (
        <SectionEmpty tone="error">
          {error instanceof Error ? error.message : 'Jurnalul nu a putut fi citit.'}
        </SectionEmpty>
      ) : data.items.length === 0 ? (
        <SectionEmpty>
          {isFiltered ? 'Niciun rezultat pentru filtrele selectate.' : 'Nicio înregistrare.'}
        </SectionEmpty>
      ) : (
        <>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className="w-[150px]">Când</Table.HeaderCell>
                <Table.HeaderCell className="w-[160px]">Autor</Table.HeaderCell>
                <Table.HeaderCell className="w-[200px]">Acțiune</Table.HeaderCell>
                <Table.HeaderCell>Obiect</Table.HeaderCell>
                <Table.HeaderCell className="w-[120px]">IP</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.items.map((item) => (
                <ActivityRow key={item.id} item={item} />
              ))}
            </Table.Body>
          </Table>

          <div className="flex items-center justify-between gap-4 px-6 py-4">
            <Text size="small" className="text-ui-fg-subtle">
              Pagina {data.page} din {lastPage} · {data.total}{' '}
              {data.total === 1 ? 'înregistrare' : 'înregistrări'}
            </Text>
            <div className="flex items-center gap-x-2">
              <Button
                size="small"
                variant="secondary"
                disabled={filters.page <= 1}
                onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
              >
                Înapoi
              </Button>
              <Button
                size="small"
                variant="secondary"
                disabled={filters.page >= lastPage}
                onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
              >
                Înainte
              </Button>
            </div>
          </div>
        </>
      )}
    </SectionContainer>
  );
}

function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-y-1">
      <label htmlFor={htmlFor} className="text-ui-fg-subtle txt-compact-small-plus">
        {label}
      </label>
      {children}
    </div>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  return (
    <Table.Row>
      <Table.Cell className="text-ui-fg-subtle whitespace-nowrap font-mono text-xs">
        {formatDateTime(item.createdAt)}
      </Table.Cell>

      {/* Fără operator: „Sistem" ori „Neautentificat", după acțiune. Vezi `actorLabel`. */}
      <Table.Cell className={item.userName ? undefined : 'text-ui-fg-subtle italic'}>
        {actorLabel(item)}
      </Table.Cell>

      <Table.Cell>{actionLabel(item.action)}</Table.Cell>

      <Table.Cell>
        <div className="flex flex-col gap-y-1 py-1">
          <div className="flex flex-wrap items-center gap-x-2">
            <Badge size="2xsmall">{entityLabel(item.entityType)}</Badge>
            {item.summary ? <span>{item.summary}</span> : null}
          </div>

          {/* Diferențele, câmp cu câmp. „A modificat clientul" fără *ce anume* nu
              răspunde la întrebarea pentru care există jurnalul. */}
          {item.changes ? (
            <dl className="flex flex-col gap-y-0.5">
              {Object.entries(item.changes).map(([field, change]) => (
                <div key={field} className="flex flex-wrap items-baseline gap-x-2">
                  <dt className="text-ui-fg-muted txt-compact-xsmall">{fieldLabel(field)}</dt>
                  <dd className="txt-compact-xsmall">
                    <span className="text-ui-fg-muted line-through">
                      {changeValueLabel(change.from, field)}
                    </span>
                    <span className="text-ui-fg-muted"> → </span>
                    <span className="text-ui-fg-base">{changeValueLabel(change.to, field)}</span>
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </Table.Cell>

      <Table.Cell className="text-ui-fg-subtle whitespace-nowrap font-mono text-xs">
        {item.ip ?? '-'}
      </Table.Cell>
    </Table.Row>
  );
}
